import React, { useState, useEffect, useId } from 'react';
import { Collapse } from 'reactstrap';
import { isOnServer, onKeyDown } from '@whitelabel/helpers/utils';
import { ReactComponent as IconChevronDown } from '@whitelabel/media/icons/small/chevron-down.svg';
import { ReactComponent as IconChevronUp } from '@whitelabel/media/icons/small/chevron-up.svg';
import { smoothScrollToAnchor, useAfterScrollHandler } from '@whitelabel/helpers/url';
import {
  StyledItemHeading,
  StyledItemInner,
  StyledAccordionItem,
  StyledItemBody,
  StyledItemHeader,
} from './styledAccordionItem';

export interface IAccordionItemProps {
  title: string;
  body: React.ReactElement | string;
  anchorId: string;
  scrollToAnchorID?: boolean;
  hasLoggedIn?: boolean;
  onTargetOrBottom?: () => void;
  onClick?: (pageSlug: string, articleTitle: string) => void;
}

const AccordionItem = ({
  body,
  title,
  anchorId,
  scrollToAnchorID,
  hasLoggedIn = false,
  onTargetOrBottom,
  onClick,
}: IAccordionItemProps): JSX.Element => {
  const hash = !isOnServer ? window.location.hash : '';
  const validHash = scrollToAnchorID || hash === `#${anchorId}`;
  const [isOpen, setIsOpen] = useState<boolean>();
  const baseId = useId();
  const panelId = `${baseId}-panel`;
  const headingId = `${baseId}-heading`;

  const toggle = () => {
    setIsOpen((prevState) => !prevState);
    if (onClick) onClick(anchorId, title);
  };
  const [triggerAfterScrollHandler] = useAfterScrollHandler(hash, onTargetOrBottom);

  useEffect(() => {
    if (validHash) {
      // We need to change the url hash and jump across different articles on PASH page
      setIsOpen(true);
      smoothScrollToAnchor(`#${anchorId}`)();
      if (!hasLoggedIn && onTargetOrBottom) {
        window.addEventListener('scroll', triggerAfterScrollHandler);
      }
    } else if (!hasLoggedIn && onTargetOrBottom) onTargetOrBottom();
    return () => window.removeEventListener('scroll', triggerAfterScrollHandler);
  }, [validHash, hasLoggedIn, onTargetOrBottom]);

  return (
    <StyledAccordionItem
      id={anchorId}
      data-test-id="accordion-item"
      tabIndex={0}
      onKeyDown={onKeyDown(toggle)}
      role="button"
      aria-expanded={isOpen}
      aria-controls={panelId}
      aria-labelledby={headingId}
    >
      <StyledItemHeader onClick={toggle}>
        <StyledItemHeading id={headingId}>
          {title}
          {isOpen ? <IconChevronUp /> : <IconChevronDown />}
        </StyledItemHeading>
      </StyledItemHeader>
      <Collapse isOpen={isOpen} id={panelId} aria-labelledby={headingId}>
        <StyledItemInner>
          <StyledItemBody data-test-id="AccordionItem-body">{body}</StyledItemBody>
        </StyledItemInner>
      </Collapse>
    </StyledAccordionItem>
  );
};

export default AccordionItem;
